import { useEffect, useRef } from 'react'
import type { BackfillExecutionLog } from '../types'

interface BackfillExecuteStepProps {
  isRunning: boolean
  isPaused: boolean
  progress: number
  total: number
  logs: BackfillExecutionLog[]
  onPause: () => void
  onResume: () => void
  onCancel: () => void
}

export function BackfillExecuteStep({
  isRunning,
  isPaused,
  progress,
  total,
  logs,
  onPause,
  onResume,
  onCancel,
}: BackfillExecuteStepProps) {
  const logEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs.length])

  const percentage = total > 0 ? Math.round((progress / total) * 100) : 0

  return (
    <div className="space-y-6">
      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Procesando clientes...
          </span>
          <span className="text-sm text-gray-500">
            {progress} / {total} ({percentage}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${
              isPaused ? 'bg-amber-500' : 'bg-primary-600'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        {isPaused && (
          <p className="text-sm text-amber-600 mt-1 font-medium">
            Pausado — haz clic en Reanudar para continuar
          </p>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        {isRunning && !isPaused && (
          <button onClick={onPause} className="btn btn-secondary">
            <svg className="w-4 h-4 mr-1.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pausar
          </button>
        )}
        {isPaused && (
          <button onClick={onResume} className="btn btn-primary">
            <svg className="w-4 h-4 mr-1.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Reanudar
          </button>
        )}
        {isRunning && (
          <button onClick={onCancel} className="btn btn-secondary text-red-600 border-red-300 hover:bg-red-50">
            Cancelar
          </button>
        )}
      </div>

      {/* Log output */}
      <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-y-auto font-mono text-sm">
        {logs.length === 0 && (
          <p className="text-gray-500">Esperando inicio...</p>
        )}
        {logs.map((log, i) => {
          const granted = log.results.filter((r) => r.status === 'granted').length
          const skipped = log.results.filter((r) => r.status === 'skipped').length
          const errors = log.results.filter((r) => r.status === 'error').length

          let statusIcon: string
          let statusColor: string

          if (errors > 0) {
            statusIcon = 'x'
            statusColor = 'text-red-400'
          } else if (skipped > 0 && granted > 0) {
            statusIcon = '~'
            statusColor = 'text-yellow-400'
          } else if (skipped > 0) {
            statusIcon = '-'
            statusColor = 'text-gray-400'
          } else {
            statusIcon = '+'
            statusColor = 'text-green-400'
          }

          const skipReasons = log.results
            .filter((r) => r.status === 'skipped' && r.reason)
            .map((r) => r.reason)
            .join(', ')

          return (
            <div key={i} className={`${statusColor} leading-relaxed`}>
              <span className="text-gray-500">[{String(i + 1).padStart(4, ' ')}]</span>{' '}
              <span>{statusIcon}</span>{' '}
              <span className="text-gray-300">{log.clientName}</span>{' '}
              <span className="text-gray-500">({log.clientRut})</span>
              <span className="text-gray-400">
                {': '}
                {granted > 0 && <span className="text-green-400">{granted} asignado{granted !== 1 ? 's' : ''}</span>}
                {skipped > 0 && (
                  <>
                    {granted > 0 && ', '}
                    <span className="text-yellow-400">{skipped} saltado{skipped !== 1 ? 's' : ''}</span>
                    {skipReasons && <span className="text-gray-500"> ({skipReasons})</span>}
                  </>
                )}
                {errors > 0 && (
                  <>
                    {(granted > 0 || skipped > 0) && ', '}
                    <span className="text-red-400">{errors} error{errors !== 1 ? 'es' : ''}</span>
                  </>
                )}
              </span>
            </div>
          )
        })}
        <div ref={logEndRef} />
      </div>
    </div>
  )
}
